import { useIdentitySignInMutation } from '../api/repositories/use-identity-signIn.mutation';
import { useAuth } from '../contexts/auth';
import { getRoleFromToken, getUserPermissions } from '../utils/token';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiError, TwoFactorType, UserService } from 'api/generated';
import clsx from 'clsx';
import { Role } from 'features/shared/model';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

type FormValues = {
  code: string;
  password: string;
};

const loginSchema = yup.object().shape({
  code: yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Login is required'),
  password: yup.string().min(3, 'Minimum 3 symbols').max(50, 'Maximum 50 symbols').required('Password is required')
});

const defaultValues: FormValues = {
  code: '',
  password: ''
};

export function Login() {
  const location = useLocation();
  const { from } = location.state || { from: { pathname: '/dashboard' } };
  const navigate = useNavigate();
  const { saveAuth, setCurrentUser, setCurrentPermissions } = useAuth();
  const [formStatus, setFormStatus] = useState('');
  const [formSuccessStatus, setFormSuccessStatus] = useState('');
  const { mutateAsync: login, isLoading: isSubmitting } = useIdentitySignInMutation();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isValid, isLoading: loading, errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(loginSchema),
    mode: 'onBlur'
  });

  useEffect(() => {
    if (location?.state?.successStatus) setFormSuccessStatus(location.state.successStatus);
  }, []);

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setShowPassword(true);
  };

  const handleMouseUp = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setShowPassword(false);
  };

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    login(
      {
        input: {
          formData: {
            Login: values.code,
            Password: values.password
          }
        }
      },
      {
        onSuccess: (auth) => {
          if (auth.token) {
            const role: Role | null = getRoleFromToken(auth.token);
            saveAuth(auth);
            setCurrentUser(auth);
            UserService.getUsersPermissions(role as number).then((permissions) => {
              setCurrentPermissions(getUserPermissions(permissions));
            });

            const redirect = new URLSearchParams(location.search).get('redirect');

            if (!redirect) {
              navigate(`${from.pathname}`);
            } else {
              navigate(redirect || '/');
            }
          } else if (auth.twoFactorType === TwoFactorType.Email || auth.twoFactorType === TwoFactorType.Sms) {
            navigate('/auth/two-factor', {
              state: {
                userId: auth.userId,
                twoFactorType: auth.twoFactorType,
                userEmail: auth.userEmail,
                userPhone: auth.userPhone,
                from: from.pathname
              }
            });
          } else if (auth.needPasswordChange) {
            navigate('/auth/change-password', {
              state: {
                userId: auth.userId,
                userEmail: auth.userEmail,
                userPhone: auth.userPhone,
                from: from.pathname
              }
            });
          }
        },
        onError: (error) => {
          saveAuth(undefined);
          let errorMessage = 'The login detail is incorrect';
          if (error instanceof ApiError) {
            if (error.body.error.message) errorMessage = error.body.error.message;
          }
          setFormStatus(errorMessage);
          console.error(error);
        }
      }
    );
  };

  return (
    <form className="form w-100" onSubmit={handleSubmit(onSubmit)} id="kt_login_signin_form">
      {/* begin::Heading */}
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">Sign In to Advance Motors Admin</h1>
      </div>
      {/* begin::Heading */}

      {formStatus && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formStatus}</div>
        </div>
      )}

      {formSuccessStatus && (
        <div className="mb-lg-15 alert alert-success">
          <div className="alert-text font-weight-bold">{formSuccessStatus}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">Login</label>
        <div className="input-group input-group-solid">
          <span className="input-group-text">
            <i className="bi bi-person-circle fs-2">
              <span className="path1"></span>
              <span className="path2"></span>
            </i>
          </span>
          <input
            readOnly
            onFocus={() => {
              const codeInput = document.getElementsByName('code')[0];
              if (codeInput) {
                codeInput.removeAttribute('readOnly');
              }
            }}
            placeholder="login"
            role="none"
            {...register('code')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              { 'is-invalid': touchedFields.code && errors.code },
              {
                'is-valid': touchedFields.code && !errors.code
              }
            )}
            type="text"
            name="code"
          />
        </div>
        {touchedFields.code && errors.code && (
          <div className="fv-plugins-message-container">
            <span role="alert" className="text-danger">
              {errors.code?.message}
            </span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <div className="d-flex justify-content-between mt-n5">
          <div className="d-flex flex-stack mb-2">
            {/* begin::Label */}
            <label className="form-label fw-bolder text-dark fs-6 mb-0">Password</label>
            {/* end::Label */}
          </div>
        </div>
        <div className="input-group input-group-solid">
          <span
            className="input-group-text cursor-pointer"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            style={{ userSelect: 'none' }}
          >
            <i className="bi bi-eye-fill fs-2">
              <span className="path1"></span>
              <span className="path2"></span>
            </i>
          </span>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="password"
            {...register('password')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': touchedFields.password && errors.password
              },
              {
                'is-valid': touchedFields.password && !errors.password
              }
            )}
          />
        </div>
        {touchedFields.password && errors.password && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert" className="text-danger">
                {errors.password?.message}
              </span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_in_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={isSubmitting || !isValid}
        >
          {!loading && !isSubmitting && <span className="indicator-label">Continue</span>}
          {(loading || isSubmitting) && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      <div className="d-flex align-items-center justify-content-center w-100 mt-7">
        <Link to="/auth/forgot-password" className="link-danger">
          <p>Forgot your password?</p>
        </Link>
      </div>
      {/* end::Action */}
    </form>
  );
}
